// DEM-08 (multi-provider v5): Wizard de Instanciação de agent global.
// 4 passos click-only. NÃO confundir com /agents/new (Wizard de criação).
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type CanonicalEffort } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ProviderSelect } from '@/components/llm/ProviderSelect';
import { ModelSelect } from '@/components/llm/ModelSelect';
import { ReasoningSlider } from '@/components/llm/ReasoningSlider';
import { CheckCircle2, ArrowLeft, ArrowRight } from 'lucide-react';

export function AgentInstantiateWizardPage() {
  const { id: agentId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('');
  const [supportedLevels, setSupportedLevels] = useState<CanonicalEffort[]>([]);
  const [defaultLevel, setDefaultLevel] = useState<CanonicalEffort | undefined>();
  const [effort, setEffort] = useState<CanonicalEffort | undefined>();
  const [userMd, setUserMd] = useState('');
  const [userMdSource, setUserMdSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Pré-popula display_name a partir do agent global
  const { data: agent } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => api.getAgent(agentId!),
    enabled: !!agentId,
  });

  useEffect(() => {
    if (agent?.name && !displayName) setDisplayName(agent.name);
  }, [agent, displayName]);

  // Resolve user_md do companion mais ativo do tenant
  const { data: wizardResolve } = useQuery({
    queryKey: ['wizard-resolve'],
    queryFn: () => api.resolveWizardUserMd(),
    enabled: step === 4,
    staleTime: 0,
  });

  useEffect(() => {
    if (wizardResolve) {
      setUserMd(wizardResolve.user_md ?? '');
      setUserMdSource(wizardResolve.source);
    }
  }, [wizardResolve]);

  async function handleSubmit() {
    if (!agentId || !provider || !model) {
      setError('Provider e modelo obrigatórios.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const overrides: Record<string, unknown> = { provider, model_key: model };
      if (effort) overrides['reasoning_effort'] = effort;
      const inst = await api.createAgentInstance({
        agent_id: agentId,
        ...(displayName ? { display_name: displayName } : {}),
        config_overrides: overrides as Parameters<typeof api.createAgentInstance>[0]['config_overrides'],
        ...(userMd ? { user_md_override: userMd } : {}),
      });
      navigate(`/agents/${inst.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar instância');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <PageHeader
        title={`Instanciar: ${agent?.name ?? '…'}`}
        description="Cria uma instância no seu tenant. Diferente do Wizard de criação, aqui você só configura — o agente já existe."
      />

      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className={`flex-1 h-1 rounded ${s <= step ? 'bg-raios-accent' : 'bg-raios-border'}`} />
        ))}
      </div>
      <p className="text-xs text-raios-muted">Passo {step} de 4</p>

      <Card className="p-6 space-y-4">
        {step === 1 && (
          <>
            <h3 className="font-semibold">1. Nome da instância</h3>
            <p className="text-xs text-raios-muted">
              Como esta instância vai aparecer na lista. Pode ter múltiplas com o mesmo agent (ex: "Dex Opus 4.7" + "Dex GPT-5.5").
            </p>
            <Input
              label="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={agent?.name ?? 'Ex: Dex Opus 4.7'}
            />
          </>
        )}

        {step === 2 && (
          <>
            <h3 className="font-semibold">2. Provider e modelo</h3>
            <ProviderSelect
              value={provider}
              onChange={(v) => { setProvider(v); setModel(''); setSupportedLevels([]); setDefaultLevel(undefined); setEffort(undefined); }}
            />
            <ModelSelect
              provider={provider}
              value={model}
              onChange={(v, m) => {
                setModel(v);
                if (m) {
                  setSupportedLevels(m.supported_effort_levels);
                  setDefaultLevel(m.default_effort_level ?? undefined);
                  setEffort(m.default_effort_level ?? undefined);
                }
              }}
            />
          </>
        )}

        {step === 3 && (
          <>
            <h3 className="font-semibold">3. Reasoning effort</h3>
            <p className="text-xs text-raios-muted">
              Escolha o nível de raciocínio. Default = 2º maior nível do modelo top.
            </p>
            <ReasoningSlider
              value={effort}
              defaultLevel={defaultLevel}
              supportedLevels={supportedLevels}
              onChange={setEffort}
            />
          </>
        )}

        {step === 4 && (
          <>
            <h3 className="font-semibold">4. Revisão e user_md</h3>
            <div className="text-xs text-raios-muted space-y-1">
              <p><strong>Display name:</strong> {displayName}</p>
              <p><strong>Provider:</strong> {provider}</p>
              <p><strong>Modelo:</strong> {model}</p>
              <p><strong>Reasoning:</strong> {effort ?? '(default do modelo)'}</p>
            </div>
            <div className="text-xs text-raios-muted">
              {userMdSource === 'empty'
                ? <span className="text-raios-warning">Nenhum companion ativo encontrado. user_md vazio — preencha do zero ou crie um companion antes.</span>
                : userMdSource.startsWith('companion:')
                ? <span>user_md puxado do companion mais ativo do seu tenant.</span>
                : null}
            </div>
            <textarea
              className="w-full min-h-[200px] rounded-md border border-raios-border bg-raios-surface p-3 text-sm text-raios-text"
              placeholder="user_md (markdown sobre você que o agente vai usar)…"
              value={userMd}
              onChange={(e) => setUserMd(e.target.value)}
            />
          </>
        )}

        {error && (
          <p className="text-xs text-raios-danger-text bg-raios-danger/10 rounded px-3 py-2">{error}</p>
        )}

        <div className="flex justify-between pt-2">
          <Button
            variant="ghost"
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            disabled={submitting}
          >
            <ArrowLeft className="size-4" />
            {step === 1 ? 'Voltar' : 'Anterior'}
          </Button>

          {step < 4 ? (
            <Button
              variant="brand"
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 2 && (!provider || !model))
              }
            >
              Próximo
              <ArrowRight className="size-4" />
            </Button>
          ) : (
            <Button variant="brand" onClick={handleSubmit} loading={submitting}>
              <CheckCircle2 className="size-4" />
              Criar instância
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
